import React, { useEffect, useState } from 'react';
import PageLayout from 'components/layouts/PageLayout';
import ServiceHelper from 'stores/ServiceHelper';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { TagModel } from 'types/CommonTypes';
import { useStoreWrapper } from 'stores/StoreHelper';
import { useStateContext } from 'providers/usePageState';
import TagForm from 'components/pages/tags/TagForm';
import TagItem from 'components/pages/listings/TagItem';
import { InnerGroup, InnerSection } from 'components/layouts/CommonForm';
import Text from 'components/commons/Text';
import { Button, Row } from 'saladsoft-primitive';
import { useMultiSelect } from 'components/multiSelectContainer/useMultiSelect';
import { ItemType } from 'components/multiSelectContainer/types';
import { merge } from 'lodash';
import { toast } from 'react-toastify';

const TagPage = () => {
  const { listingId } = useParams();
  const { uiStore } = useStoreWrapper();
  const { broadcast } = useStateContext();
  const navigate = useNavigate();

  // 전역 태그
  const [tags, setTags] = useState<TagModel[]>([]);
  // 숙소에 지정된 태그
  const [listingTags, setListingTags] = useState<TagModel[]>([]);
  // 숙소에 지정된 태그 + 전역 태그
  const [mergedTags, setMergedTags] = useState<TagModel[]>([]);

  const addAllTags = () => {
    // 모든 태그가 선택된 상태인지 확인
    const isAllSelected = mergedTags.every((tag) => tag.isChecked);

    // 전체 선택 해제인 경우 빈 배열을, 아닌 경우 모든 태그 ID를 전달
    const targetTags = isAllSelected ? [] : (tags.map((tag) => tag.id) as number[]);

    ServiceHelper.adminListingService?.saveTags(Number(listingId), targetTags).then((res) => {
      if (res.status === 204) {
        toast.success('태그가 적용되었습니다.');
        fetchTags();
      }
    });
  };

  /**
   * @description 태그 체크 상태 변경
   * @param tag
   */
  const handleOnChange = async (tag: TagModel) => {
    const updatedTags = mergedTags.map((item) => {
      if (item.id === tag.id) {
        return { ...item, isChecked: !item.isChecked };
      }
      return item;
    });

    setMergedTags(updatedTags);

    await ServiceHelper.adminListingService?.saveTags(
      Number(listingId),
      updatedTags.filter((item) => item.isChecked).map((item) => item.id) as number[],
    );

    broadcast({ id: 'reload', param: 'tags' });
  };

  const handleAddTag = () => {
    uiStore?.modal.show({
      title: '태그 추가',
      style: { width: 800, maxHeight: '80%' },
      children: (
        <div style={{ padding: 20 }}>
          <TagForm
            onSave={async (tag: TagModel) => {
              await fetchSystemTags();
            }}
          />
        </div>
      ),
    });
  };

  const fetchTags = async () => {
    if (!tags.length) return;

    ServiceHelper.adminListingService?.getTagsByListing1(Number(listingId)).then((res) => {
      const updatedTags = tags.map((apiTag) => ({
        ...apiTag,
        isChecked: res?.data?.some((item) => item.id === apiTag.id),
      }));
      setListingTags(res.data as TagModel[]);
      // 깊은 복사를 통해 새로운 참조를 생성
      const newTags = JSON.parse(JSON.stringify(updatedTags));
      setMergedTags(newTags);
    });
  };

  const fetchSystemTags = async () => {
    ServiceHelper.adminTagService?.getTags1().then((res) => {
      setTags(res.data as TagModel[]);
    });
  };

  useEffect(() => {
    fetchSystemTags();
  }, []);

  useEffect(() => {
    fetchTags();
  }, [tags]);

  return (
    <PageLayout title="태그">
      <InnerSection>
        <InnerGroup style={{ minHeight: 'unset' }}>
          <div>
            <Text type="H3" style={{ marginBottom: 10 }}>
              태그지정하기
            </Text>
            <Text type="H4" buttonRole="DESCRIPTION" style={{ marginBottom: 10 }}>
              우측의 태그 목록에서 등록하실 태그를 선택 해주세요.
              <br />
              선택 즉시 반영됩니다.
              <br />
              <br />
              관련된 태그가 없을경우, 아래 추가 버튼을 눌러 <br />
              신규 태그를 추가해주세요. <br />
              <br />
              *추가된 태그는 모든 시설에서 사용 가능합니다.
            </Text>
            <Row width="100%" justifyContent="flex-start">
              <Button
                type="outlined"
                style={{ width: 150, marginTop: 20 }}
                onClick={() => handleAddTag()}
              >
                추가하기
              </Button>
              <Button
                type="link"
                style={{ width: 200, marginTop: 20 }}
                onClick={() => {
                  window.open('/admin/checkin-guide/tag-settings', '_blank');
                }}
              >
                태그관리 페이지(새창)
              </Button>
            </Row>
          </div>
          <div style={{ width: '100%', flex: 1 }}>
            <Row marginBottom="20px" justifyContent="flex-start" gap="10px">
              <Button type="outlined" style={{ width: 150 }} onClick={() => addAllTags()}>
                {mergedTags.filter((tag) => tag.isChecked).length === tags.length
                  ? '전체 선택해제'
                  : '전체추가'}
              </Button>
            </Row>
            <Row flexWrap="wrap" width="100%" justifyContent="flex-start">
              {mergedTags?.map((tag) => (
                <>
                  <TagItem
                    defaultChecked={tag.isChecked}
                    tag={tag}
                    onChange={(tag) => handleOnChange(tag)}
                  />
                </>
              ))}
            </Row>
          </div>
        </InnerGroup>
      </InnerSection>
    </PageLayout>
  );
};

const Wrapper = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 10px 20px;
  border: 1px solid #efefef;
  gap: 10px;
  margin: 10px;
  border-radius: 10px;
`;

export default TagPage;
